/**
 * FusionGoalWizard — Quick 2-step wizard for fitness_goal + activity_level.
 * Shown on first PhaseExecute load if user has no preferences.
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Zap, X, TrendingDown, Repeat, TrendingUp, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const GOALS = [
  { key: "cut", label: "Perder gordura", desc: "Défice calórico estruturado", Icon: TrendingDown, color: "#f97316" },
  { key: "recomp", label: "Recomposição", desc: "Perder gordura + ganhar músculo", Icon: Repeat, color: "#84cc16" },
  { key: "maintain", label: "Manter", desc: "Estabilizar composição actual", Icon: Target, color: "#06b6d4" },
  { key: "bulk", label: "Ganhar massa", desc: "Superávit controlado", Icon: TrendingUp, color: "#a855f7" },
];

const ACTIVITY = [
  { key: "sedentary", label: "Sedentário", desc: "Trabalho de escritório, pouco movimento", factor: "1.375×" },
  { key: "light", label: "Ligeiro", desc: "1-2 treinos/sem + caminhadas", factor: "1.5×" },
  { key: "moderate", label: "Moderado", desc: "3-4 treinos/sem consistentes", factor: "1.625×" },
  { key: "athlete", label: "Atleta", desc: "5+ treinos/sem, força + cardio", factor: "1.725×" },
];

export default function FusionGoalWizard({ open, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState(null);
  const [activity, setActivity] = useState(null);
  const [saving, setSaving] = useState(false);

  const canProceed = step === 1 ? !!goal : !!activity;

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/user/fitness-preferences`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ fitness_goal: goal, activity_level: activity }),
      });
      toast.success("Preferências guardadas ✓");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        data-testid="fusion-goal-wizard"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
          className="bg-zinc-950 border border-lime-500/30 rounded-3xl p-6 max-w-md w-full"
          style={{ boxShadow: "0 20px 60px rgba(132,204,22,0.2)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-lime-400 mb-1">Passo {step} de 2</p>
              <h3 className="text-lg font-bold text-zinc-100">
                {step === 1 ? "Qual o teu objectivo?" : "Nível de actividade?"}
              </h3>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100" data-testid="wizard-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {step === 1 && (
            <div className="space-y-2 mb-5">
              {GOALS.map((g) => {
                const Icon = g.Icon;
                const active = goal === g.key;
                return (
                  <button
                    key={g.key}
                    onClick={() => setGoal(g.key)}
                    className="w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3"
                    style={{
                      borderColor: active ? g.color : "#27272a",
                      background: active ? `${g.color}15` : "transparent",
                      boxShadow: active ? `0 0 15px ${g.color}30` : "none",
                    }}
                    data-testid={`wizard-goal-${g.key}`}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${g.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: g.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-zinc-100">{g.label}</p>
                      <p className="text-[11px] text-zinc-400">{g.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2 mb-5">
              {ACTIVITY.map((a) => {
                const active = activity === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => setActivity(a.key)}
                    className="w-full p-3 rounded-xl border text-left transition-all"
                    style={{
                      borderColor: active ? "#84cc16" : "#27272a",
                      background: active ? "rgba(132,204,22,0.1)" : "transparent",
                    }}
                    data-testid={`wizard-activity-${a.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <Activity className="w-4 h-4 text-lime-400" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-zinc-100 flex items-center gap-2">
                          {a.label}
                          <span className="text-[10px] font-mono text-lime-400">{a.factor}</span>
                        </p>
                        <p className="text-[11px] text-zinc-400">{a.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2">
            {step === 2 && (
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1 border-zinc-700 text-zinc-300" data-testid="wizard-back">
                Voltar
              </Button>
            )}
            <Button
              onClick={step === 1 ? () => setStep(2) : save}
              disabled={!canProceed || saving}
              className="flex-1 bg-gradient-to-r from-lime-500 to-emerald-500 text-black font-bold disabled:opacity-50"
              data-testid="wizard-next"
            >
              {saving ? "A guardar..." : step === 1 ? "Continuar" : "Guardar"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
